// src/components/SearchBar.tsx
import {
  Box,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Text,
  VStack,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  MagnifyingGlassIcon,
  ArrowUpRightIcon,
  XIcon,
} from "@phosphor-icons/react";
import { RecordingCategory } from "../../model/recording";
import {
  RecordingTitleSearchItem,
  SearchSuggestionCategory,
} from "../../model/search";
import { fakeSearchRecordingTitles } from "../../services/search";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import EmptyState from "../placeholders/EmptyState";

type SearchMode = "local" | "fetch";

const getCategoryLabel = (category: SearchSuggestionCategory, t: any) => {
  if (category === "teaching") return t("menu.teachings");
  if (category === "zoom_prayer") return t("menu.zoomPrayer");
  if (category === "prayer_vigil") return t("menu.nightVigils");
  if (category === "hymn") return t("menu.hymns");
  return t("menu.exhortations");
};

const searchHeight = { base: "280px", md: "400px" };

const getCategoryForRoute = (
  pathname: string,
): RecordingCategory | "hymn" | undefined => {
  if (pathname === "/teachings") return "teaching";
  if (pathname === "/hymns") return "hymn";
  if (pathname === "/exhortations") return "exhortation";
  if (pathname === "/zoom-prayers") return "zoom_prayer";
  if (pathname === "/night-vigils") return "prayer_vigil";
  return undefined;
};

const toEpochMs = (timestamp: number): number =>
  timestamp > 1_000_000_000_000 ? timestamp : timestamp * 1000;

const highlightQuery = (title: string, query: string) => {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) {
    return [{ value: title, highlighted: false }];
  }

  const escaped = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matcher = new RegExp(`(${escaped})`, "ig");
  const chunks = title.split(matcher).filter(Boolean);

  return chunks.map((chunk) => ({
    value: chunk,
    highlighted: chunk.toLowerCase() === normalizedQuery.toLowerCase(),
  }));
};

export default function SearchBar({
  placeholder,
  onSearch,
  value,
  mb,
  sharedRow = false,
  mode = "fetch",
  debounceMs = 500,
  fetchSuggestions,
  onSuggestionSelect,
}: {
  onSearch?: (value: string) => void;
  placeholder?: string;
  value?: string;
  mb?: number;
  sharedRow?: boolean;
  mode?: SearchMode;
  debounceMs?: number;
  fetchSuggestions?: (
    query: string,
    options?: { category?: string },
  ) => Promise<RecordingTitleSearchItem[]>;
  onSuggestionSelect?: (item: RecordingTitleSearchItem) => void;
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = i18n.language;
  const [internalValue, setInternalValue] = useState("");
  const [suggestions, setSuggestions] = useState<RecordingTitleSearchItem[]>(
    [],
  );
  const [isSearchingSuggestions, setIsSearchingSuggestions] = useState(false);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const isControlled = typeof value === "string";
  const currentValue = isControlled ? value : internalValue;
  const suggestionCategory = useMemo(
    () =>
      location.pathname === "/search"
        ? undefined
        : getCategoryForRoute(location.pathname),
    [location.pathname],
  );
  const suggestionDebounceMs = debounceMs > 0 ? debounceMs : 500;
  const fetchSuggestionsRef = useRef(
    fetchSuggestions ?? fakeSearchRecordingTitles,
  );

  useEffect(() => {
    fetchSuggestionsRef.current = fetchSuggestions ?? fakeSearchRecordingTitles;
  }, [fetchSuggestions]);

  useEffect(() => {
    if (mode !== "fetch") {
      return;
    }

    const query = currentValue.trim();
    if (!query) {
      setSuggestions((prev) => (prev.length === 0 ? prev : []));
      setIsSearchingSuggestions((prev) => (prev ? false : prev));
      setIsSuggestionsOpen((prev) => (prev ? false : prev));
      setDebouncedQuery("");
      return;
    }

    const timer = window.setTimeout(() => {
      setDebouncedQuery(query);
      setIsSuggestionsOpen(true);
    }, suggestionDebounceMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [currentValue, mode, suggestionDebounceMs]);

  useEffect(() => {
    if (mode !== "fetch" || !debouncedQuery) {
      return;
    }

    let canceled = false;
    setIsSearchingSuggestions(true);

    const loadSuggestions = async () => {
      try {
        const result = await fetchSuggestionsRef.current(debouncedQuery, {
          category: suggestionCategory,
        });
        if (canceled) {
          return;
        }
        setSuggestions(result);
      } catch {
        if (canceled) {
          return;
        }
        setSuggestions([]);
      } finally {
        if (!canceled) {
          setIsSearchingSuggestions(false);
        }
      }
    };

    loadSuggestions();

    return () => {
      canceled = true;
    };
  }, [debouncedQuery, mode, suggestionCategory]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!isControlled) {
      setInternalValue(val);
    }
    onSearch?.(val);
  };

  const clearSearch = () => {
    if (!isControlled) {
      setInternalValue("");
    }
    setSuggestions([]);
    setIsSuggestionsOpen(false);
    onSearch?.("");
  };

  const handleSuggestionClick = (item: RecordingTitleSearchItem) => {
    if (!isControlled) {
      setInternalValue("");
    }
    onSuggestionSelect?.(item);
    setSuggestions([]);
    setIsSuggestionsOpen(false);
  };

  const bg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const suggestionHoverBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      w={sharedRow ? "50%" : "full"}
      flex={sharedRow ? "0 0 50%" : "1 1 auto"}
      minW="0"
      position="relative"
      mb={mb}
    >
      <InputGroup w="full">
        <InputLeftElement pointerEvents="none">
          <MagnifyingGlassIcon size={20} color="gray" />
        </InputLeftElement>

        <Input
          type="text"
          placeholder={placeholder ?? String(t("common.searchPlaceholder"))}
          value={currentValue}
          onChange={handleChange}
          onBlur={() => {
            window.setTimeout(() => setIsSuggestionsOpen(false), 150);
          }}
          borderRadius="4px"
          bg={bg}
          color={textColor}
          _focus={{
            borderColor: "primary.500",
            boxShadow: "none",
            borderWidth: "1.5px",
          }}
        />

        {currentValue && (
          <InputRightElement>
            <IconButton
              aria-label={String(t("common.clearSearch"))}
              icon={<XIcon size={16} />}
              size="sm"
              variant="ghost"
              onClick={clearSearch}
            />
          </InputRightElement>
        )}
      </InputGroup>

      {mode === "fetch" &&
        isSuggestionsOpen &&
        Boolean(currentValue.trim()) && (
          <VStack
            w={sharedRow ? "200%" : "full"}
            position="absolute"
            top="calc(100% + 6px)"
            left={0}
            zIndex={1400}
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="8px"
            align="stretch"
            spacing={0}
            maxH={searchHeight}
            overflowY="auto"
            overflowX="hidden"
            boxShadow="md"
          >
            {isSearchingSuggestions ? (
              <VStack w="full" justify="center" h={searchHeight}>
                <Spinner size="lg" />
              </VStack>
            ) : suggestions.length === 0 ? (
              <EmptyState
                icon={MagnifyingGlassIcon}
                title={t("pages.search.noResultsTitle")}
                description={t("pages.search.noResultsDescription")}
              />
            ) : (
              suggestions.map((item) => {
                const chunks = highlightQuery(item.title, currentValue);
                return (
                  <HStack
                    key={item.sortKey}
                    px={3}
                    py={2}
                    spacing={3}
                    cursor="pointer"
                    align="start"
                    _hover={{ bg: suggestionHoverBg }}
                    onClick={() => handleSuggestionClick(item)}
                  >
                    <Icon
                      as={ArrowUpRightIcon}
                      boxSize={5}
                      color="gray.500"
                      mt="2px"
                      flexShrink={0}
                    />
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                      <Text
                        fontSize="15px"
                        color={textColor}
                        whiteSpace="normal"
                        wordBreak="break-word"
                      >
                        {chunks.map((chunk, index) => (
                          <Text
                            as="span"
                            key={`${item.recordingID}-${index}`}
                            fontWeight={chunk.highlighted ? "700" : "400"}
                            color={
                              chunk.highlighted ? "primary.500" : textColor
                            }
                          >
                            {chunk.value}
                          </Text>
                        ))}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {getCategoryLabel(item.category, t)}
                      </Text>
                    </VStack>
                    {item.date && (
                      <Text fontSize="xs" color="gray.500" flexShrink={0}>
                        {new Date(toEpochMs(item.date)).toLocaleDateString(
                          lang,
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          },
                        )}
                      </Text>
                    )}
                  </HStack>
                );
              })
            )}
          </VStack>
        )}
    </Box>
  );
}
